import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  makeStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  row: {
    display: "flex",
    gap: theme.spacing(2),
    "& > *": {
      flex: 1,
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: 0,
    },
  },
  deleteButton: {
    marginRight: "auto",
    color: theme.palette.error.main,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";
const PRIORITIES = ["low", "normal", "high"];

// Busca com debounce para os autocompletes de usuário e contato
const useRemoteOptions = (open, url, listKey, filter = (x) => true) => {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(url, {
          params: { searchParam: search, pageNumber: 1 },
        });
        setOptions(data[listKey].filter(filter));
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search, url, listKey]);

  return { options, loading, setSearch };
};

const emptyTask = (user, initialDueAt, initialContact) => ({
  title: "",
  description: "",
  dueAt: initialDueAt ? format(initialDueAt, INPUT_FORMAT) : "",
  priority: "normal",
  status: "open",
  assignedUser: user ? { id: user.id, name: user.name } : null,
  contact: initialContact || null,
  createdById: user?.id,
});

const TaskModal = ({ open, onClose, taskId, initialDueAt, initialContact }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [task, setTask] = useState(emptyTask(user));
  const [saving, setSaving] = useState(false);

  const users = useRemoteOptions(open, "/users", "users");
  const contacts = useRemoteOptions(open, "/contacts", "contacts", (c) => !c.isGroup);

  useEffect(() => {
    if (!open) return;
    if (!taskId) {
      setTask(emptyTask(user, initialDueAt, initialContact));
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/tasks/${taskId}`);
        setTask({
          title: data.title,
          description: data.description || "",
          dueAt: data.dueAt ? format(parseISO(data.dueAt), INPUT_FORMAT) : "",
          priority: data.priority,
          status: data.status,
          assignedUser: data.assignedUser,
          contact: data.contact,
          createdById: data.createdById,
        });
      } catch (err) {
        toastError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  const setField = (field) => (value) =>
    setTask((prev) => ({ ...prev, [field]: value }));

  const canDelete =
    taskId && (user.profile === "admin" || task.createdById === user.id);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: task.title,
      description: task.description,
      dueAt: task.dueAt ? new Date(task.dueAt).toISOString() : null,
      priority: task.priority,
      assignedUserId: task.assignedUser?.id || null,
      contactId: task.contact?.id || null,
      ...(taskId && { status: task.status }),
    };
    try {
      if (taskId) {
        await api.put(`/tasks/${taskId}`, payload);
      } else {
        await api.post("/tasks", payload);
      }
      toast.success(i18n.t("tasks.toasts.saved"));
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success(i18n.t("tasks.toasts.deleted"));
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t(taskId ? "taskModal.title.edit" : "taskModal.title.add")}
      </DialogTitle>
      <form onSubmit={handleSave}>
        <DialogContent dividers>
          <TextField
            className={classes.field}
            label={i18n.t("taskModal.form.title")}
            value={task.title}
            onChange={(e) => setField("title")(e.target.value)}
            variant="outlined"
            fullWidth
            required
            autoFocus
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            className={classes.field}
            label={i18n.t("taskModal.form.description")}
            value={task.description}
            onChange={(e) => setField("description")(e.target.value)}
            variant="outlined"
            fullWidth
            multiline
            minRows={3}
          />
          <div className={classes.row}>
            <TextField
              className={classes.field}
              label={i18n.t("taskModal.form.dueAt")}
              type="datetime-local"
              value={task.dueAt}
              onChange={(e) => setField("dueAt")(e.target.value)}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className={classes.field}
              select
              label={i18n.t("taskModal.form.priority")}
              value={task.priority}
              onChange={(e) => setField("priority")(e.target.value)}
              variant="outlined"
            >
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {i18n.t(`tasks.priority.${p}`)}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className={classes.row}>
            <Autocomplete
              className={classes.field}
              options={users.options}
              value={task.assignedUser}
              loading={users.loading}
              getOptionLabel={(option) => option.name || ""}
              getOptionSelected={(option, value) => option.id === value.id}
              filterOptions={(x) => x}
              onChange={(e, value) => setField("assignedUser")(value)}
              onInputChange={(e, value, reason) => {
                if (reason === "input") users.setSearch(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("taskModal.form.assignedUser")}
                  variant="outlined"
                />
              )}
            />
            <Autocomplete
              className={classes.field}
              options={contacts.options}
              value={task.contact}
              loading={contacts.loading}
              getOptionLabel={(option) => option.name || ""}
              getOptionSelected={(option, value) => option.id === value.id}
              filterOptions={(x) => x}
              onChange={(e, value) => setField("contact")(value)}
              onInputChange={(e, value, reason) => {
                if (reason === "input") contacts.setSearch(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("taskModal.form.contact")}
                  variant="outlined"
                />
              )}
            />
          </div>
          {taskId && (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={task.status === "done"}
                  onChange={(e) =>
                    setField("status")(e.target.checked ? "done" : "open")
                  }
                />
              }
              label={i18n.t("taskModal.form.done")}
            />
          )}
        </DialogContent>
        <DialogActions>
          {canDelete && (
            <Button onClick={handleDelete} className={classes.deleteButton}>
              {i18n.t("taskModal.buttons.delete")}
            </Button>
          )}
          <Button onClick={onClose} disabled={saving} variant="outlined">
            {i18n.t("taskModal.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={saving}
            className={classes.btnWrapper}
          >
            {i18n.t("taskModal.buttons.ok")}
            {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TaskModal;
